import { FC, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { SentimentDistributionCard } from 'src/components/SentimentDistributionCard'
import { ReviewStatsCards } from 'src/pages/ReviewAnalysis/BrandLevelWidgets/ReviewStatsCards'
import { PLATFORM_LABEL } from 'src/pages/ReviewAnalysis/constants'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import { useGetReviewAnalysisFacilityActivityQuery } from 'src/reactQuery/useGetReviewAnalysisFacilityActivityQuery'
import { useGetReviewAnalysisFacilityOverviewQuery } from 'src/reactQuery/useGetReviewAnalysisFacilityOverviewQuery'
import { useGetReviewAnalysisPlatformAnalysisQuery } from 'src/reactQuery/useGetReviewAnalysisPlatformAnalysisQuery'
import { useGetReviewAnalysisReviewsQuery } from 'src/reactQuery/useGetReviewAnalysisReviewsQuery'
import { useReviewAnalysisDateRange } from 'src/hooks/useReviewAnalysisDateRange'
import { ReviewPlatform } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './facilityReviewAnalysis.module.scss'
import { LocationFilter } from '../../LocationFilter'
import { LocationComparison } from '../../PlatformLevelWidgets/PlatformReviewAnalysis/LocationComparison'
import { PerformanceLocations } from '../../PlatformLevelWidgets/PlatformReviewAnalysis/PerformanceLocations'
import { RatingAnalytics } from '../../PlatformLevelWidgets/PlatformReviewAnalysis/RatingAnalytics'
import { ReviewVelocity } from '../../PlatformLevelWidgets/PlatformReviewAnalysis/ReviewVelocity'

interface TabMeta {
  icon: FC<React.SVGProps<SVGSVGElement>> | null
  label: string
  platformId: ReviewPlatform | 'all'
}

const ALL_TAB: TabMeta = {
  icon: null,
  label: 'All Platforms',
  platformId: 'all',
}

// Canonical left-to-right tab order. Which of these actually render is driven
// by the facility's data, not this list.
const PLATFORM_ORDER: ReviewPlatform[] = [
  'tripadvisor',
  'booking',
  'google',
  'expedia',
  'playstore',
  'appstore',
]

// Tabs are narrow, so a couple of platforms get a shortened label here.
const TAB_LABEL_OVERRIDE: Partial<Record<ReviewPlatform, string>> = {
  booking: 'Booking',
}

const PLATFORM_TABS = PLATFORM_ORDER.reduce(
  (acc, id) => {
    acc[id] = {
      icon: getPlatformIcon(id),
      label: TAB_LABEL_OVERRIDE[id] ?? PLATFORM_LABEL[id],
      platformId: id,
    }
    return acc
  },
  {} as Record<ReviewPlatform, TabMeta>
)

export const FacilityReviewAnalysis: FC = () => {
  const { campaignId = '', facilityId = '' } = useParams<{
    campaignId: string
    facilityId: string
  }>()
  const [searchParams, setSearchParams] = useSearchParams()
  // A per-platform "View Details" link deep-links here with ?platform=<id>;
  // open that platform's tab immediately (falling back to All Platforms).
  const requestedPlatform = searchParams.get('platform')
  // ?venue=<venueName> scopes every widget on the page to one location. The
  // Location filter in the header writes it; a location card's "View
  // Details" deep-links with it.
  const requestedVenue = searchParams.get('venue') || undefined
  const isVenueLocked = !!requestedVenue
  const setVenue = (venue: string | undefined) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (venue) next.set('venue', venue)
        else next.delete('venue')
        return next
      },
      { replace: true }
    )
  }

  // The tab lives in the URL rather than in component state, because it is read
  // by TWO widgets: this one scopes its headline stats, and ReviewSmartInsights
  // filters the insights panel by the same platform. Local state would leave the
  // panel on whatever the URL said at mount while these stats moved, and the two
  // would silently disagree after the first click. The URL also keeps the tab
  // shareable and survives a refresh, which the deep-link already assumed.
  const activeTab: TabMeta['platformId'] =
    requestedPlatform && requestedPlatform in PLATFORM_TABS
      ? (requestedPlatform as ReviewPlatform)
      : 'all'
  const setActiveTab = (platformId: TabMeta['platformId']) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        // 'all' is the default, so it is absent from the URL rather than
        // written as a value — keeps a shared link clean and matches how the
        // date filter treats its own defaults.
        if (platformId === 'all') next.delete('platform')
        else next.set('platform', platformId)
        return next
      },
      { replace: true }
    )
  }

  const dateRange = useReviewAnalysisDateRange()
  // Active tab scopes the headline stats (Total Reviews / Average Rating /
  // Sentiment Distribution) to that platform; 'all' leaves them unscoped. The
  // response's byPlatform stays all-platform, so the tab list itself is stable.
  const scopedPlatformId = activeTab === 'all' ? undefined : activeTab
  // When opened from a location card, scope every widget to that one venue.
  const scopedVenueName = requestedVenue
  const {
    data: facilityOverview,
    isError: isOverviewMissing,
    isFetching: isOverviewFetching,
  } = useGetReviewAnalysisFacilityOverviewQuery(
    campaignId,
    facilityId,
    dateRange,
    scopedPlatformId,
    scopedVenueName
  )
  // The whole facility (the page's own read, shared through react-query): the
  // title and the platform tabs come from it, so a location with no reviews in
  // the period (its read is a 404) or on the active platform neither blanks
  // the header nor leaves the active tab undrawn.
  const { data: facilityWide } = useGetReviewAnalysisFacilityOverviewQuery(
    campaignId,
    facilityId,
    dateRange
  )

  // Tabs are driven by the platforms this facility actually has reviews on, in a
  // fixed display order — so e.g. Expedia only appears when there's Expedia data.
  const tabs = useMemo<TabMeta[]>(() => {
    const present = new Set(
      ((facilityWide ?? facilityOverview)?.byPlatform ?? []).map(
        (p) => p.platform
      )
    )
    if (activeTab !== 'all') present.add(activeTab)
    return [
      ALL_TAB,
      ...PLATFORM_ORDER.filter((p) => present.has(p)).map(
        (p) => PLATFORM_TABS[p]
      ),
    ]
  }, [activeTab, facilityOverview, facilityWide])

  const currentTab = activeTab === 'all' ? ALL_TAB : PLATFORM_TABS[activeTab]
  const isAllPlatforms = activeTab === 'all'
  const providerName = isAllPlatforms ? 'All Platforms' : currentTab.label
  const ProviderIcon = currentTab.icon || null
  const reviewsPlatform: ReviewPlatform | undefined = isAllPlatforms
    ? undefined
    : activeTab
  const platformParam = reviewsPlatform
  const { data: facilityActivity, isFetching: isActivityFetching } =
    useGetReviewAnalysisFacilityActivityQuery(
      campaignId,
      facilityId,
      platformParam,
      dateRange,
      scopedVenueName
    )
  const { data: platformAnalysis, isFetching: isPlatformAnalysisFetching } =
    useGetReviewAnalysisPlatformAnalysisQuery(
      campaignId,
      currentTab.platformId,
      facilityId,
      dateRange,
      scopedVenueName
    )

  // The filter's options: every location of the facility in the period,
  // whatever location is selected — the reviews read's branch counts (one
  // row asked for; the counts cover the whole facility).
  const { data: branchData } = useGetReviewAnalysisReviewsQuery(campaignId, {
    facilityId,
    limit: 1,
    ...(dateRange ?? {}),
  })
  const locationNames = useMemo(() => {
    const names = new Set(
      (branchData?.pages[0]?.branchCounts ?? [])
        .map((b) => b.venueName)
        .filter(Boolean)
    )
    if (requestedVenue) names.add(requestedVenue)
    return Array.from(names).sort((a, b) => a.localeCompare(b))
  }, [branchData, requestedVenue])

  const facilityName = (facilityWide ?? facilityOverview)?.facility.name ?? ''
  const facilityCategory = (facilityWide ?? facilityOverview)?.facility.category

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>
            Facility Review Analysis{facilityName ? `: ${facilityName}` : ''}
          </h2>
          {/* The facility's Analysis Goal, as on its card (set when it was
              created or from Edit facility). */}
          {facilityCategory && (
            <span className={styles.categoryTag}>{facilityCategory}</span>
          )}
        </div>
        <div className={styles.headerRight}>
          {(locationNames.length > 1 || !!requestedVenue) && (
            <LocationFilter
              locations={locationNames}
              onChange={setVenue}
              value={requestedVenue}
            />
          )}
          <div className={styles.tabsContainer}>
            {tabs.map((tab) => (
              <button
                className={`${styles.tabBtn} ${activeTab === tab.platformId ? styles.tabBtnActive : ''}`}
                key={tab.platformId}
                onClick={() => setActiveTab(tab.platformId)}
                type="button"
              >
                {tab.icon && (
                  <span className={styles.tabIcon}>
                    <tab.icon />
                  </span>
                )}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {requestedVenue && (
        <p className={styles.scopeNote}>
          {isOverviewMissing && !isOverviewFetching
            ? `No reviews for ${requestedVenue}${
                scopedPlatformId
                  ? ` on ${PLATFORM_LABEL[scopedPlatformId]}`
                  : ''
              } in this period.`
            : 'Showing one location.'}{' '}
          Smart Insights and the AI Summary cover the whole facility.
        </p>
      )}

      {facilityOverview && (
        <div className={styles.statsRow}>
          {/* Change values pass through, never `?? 0`. The backend sends null
              when the previous window is empty, and ReviewStatsCards hides the
              trend badge on null — coercing to 0 forced it open reading "0%",
              asserting "no change" where no comparison exists. */}
          <ReviewStatsCards
            averageRating={facilityOverview.stats.averageRating}
            averageRatingChange={facilityOverview.stats.averageRatingChange}
            locationLabel={requestedVenue}
            totalReviews={facilityOverview.stats.totalReviews}
            totalReviewsChange={facilityOverview.stats.totalReviewsChange}
            trendLabel={dateRange ? 'vs previous period' : undefined}
          />
          <SentimentDistributionCard
            data={facilityOverview.stats.sentiment}
            isLoading={isOverviewFetching}
          />
        </div>
      )}

      {/* Best/Worst Performing Locations and the Location Comparison table
          compare locations against each other — meaningless on a single-location
          view, so hide them when scoped to one venue. */}
      {!isVenueLocked && (
        <>
          <PerformanceLocations
            bestLocations={platformAnalysis?.performance.best}
            isLoading={isPlatformAnalysisFetching}
            worstLocations={platformAnalysis?.performance.worst}
          />

          <LocationComparison
            isLoading={isPlatformAnalysisFetching}
            locations={platformAnalysis?.locations}
          />
        </>
      )}

      <RatingAnalytics
        ProviderIcon={ProviderIcon}
        averageRating={platformAnalysis?.ratingSummary?.averageRating}
        averageRatingChangePercent={
          platformAnalysis?.ratingSummary?.trendPercent
        }
        averageRatingDelta={platformAnalysis?.ratingSummary?.ratingDelta}
        distribution={platformAnalysis?.distribution}
        isLoading={isPlatformAnalysisFetching}
        mismatchCount={
          platformAnalysis?.ratingSummary?.mismatch?.negativeTextReviews
        }
        mismatchPercentage={
          platformAnalysis?.ratingSummary?.mismatch?.percentage
        }
        mismatchReviews={platformAnalysis?.mismatchReviews}
        providerName={providerName}
        trend={platformAnalysis?.trend}
      />

      {/* Mounted while the activity query is in flight so the section skeletons
          in place; it previously appeared only once the data landed, shoving
          the content below it down the page. */}
      {(facilityActivity || isActivityFetching) && (
        <ReviewVelocity
          ProviderIcon={ProviderIcon}
          alerts={facilityActivity?.alerts}
          isLoading={isActivityFetching}
          platformActivity={facilityActivity?.platformActivity}
          providerName={providerName}
          spike={facilityActivity?.spike}
          velocity={facilityActivity?.velocity}
        />
      )}
    </div>
  )
}
